import { createContext, FC, useCallback, useContext, useMemo, useState } from 'react'
import axios from 'axios'
import { useQuery } from 'react-query'
import { CellProps } from 'react-table'
import {
  createResponseContext,
  formatDateTime,
  initialQueryRequest,
  initialQueryResponse,
  QueryRequestContextProps,
  QueryState,
  stringifyRequestQuery,
  WithChildren,
} from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import {
  CustomTable,
  DataColumn,
  PlainHeader,
  StackedCell,
  TableColumn,
  TableEmptyState,
  TableErrorState,
} from 'app/components'
import { AfericaoDispositivoList } from '../../core/_models'
import { getAfericoes, getAfericoesResumo } from '../../core/_requests'

const RequestContext = createContext<QueryRequestContextProps>(initialQueryRequest)
const ResponseContext = createResponseContext<AfericaoDispositivoList>(initialQueryResponse)
const useQueryRequest = () => useContext(RequestContext)
const useQueryResponse = () => useContext(ResponseContext)

/** 404 de rota: o backend do ambiente ainda não tem a fatia de aferições (F1c). */
const rotaIndisponivel = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status === 404

const Providers: FC<WithChildren & { idDispositivo: number }> = ({ idDispositivo, children }) => {
  const [state, setState] = useState<QueryState>({ ...initialQueryRequest.state })
  const updateState = useCallback(
    (updates: Partial<QueryState>) => setState((prev) => ({ ...prev, ...updates })),
    []
  )
  const query = useMemo(() => stringifyRequestQuery(state), [state])
  const { data, isFetching, isError, refetch } = useQuery(
    ['dispositivo-afericoes', idDispositivo, query],
    () => getAfericoes(idDispositivo, query),
    { keepPreviousData: true, refetchOnWindowFocus: false }
  )

  return (
    <RequestContext.Provider value={{ state, updateState }}>
      <ResponseContext.Provider
        value={{ isLoading: isFetching, isError, refetch, response: data, query }}
      >
        {children}
      </ResponseContext.Provider>
    </RequestContext.Provider>
  )
}

const TabelaAfericoes = () => {
  const { t } = useT()
  const { response, isLoading, isError, refetch } = useQueryResponse()
  const colunas = useMemo<ReadonlyArray<TableColumn<AfericaoDispositivoList>>>(
    () => [
      {
        Header: () => <PlainHeader>{t('pagina.dispositivos.afericoes.col.data')}</PlainHeader>,
        id: 'data',
        cellWidth: '160px',
        Cell: ({ row }: CellProps<AfericaoDispositivoList>) => (
          <DataColumn data={row.original.data ?? undefined} />
        ),
      },
      {
        Header: () => <PlainHeader>{t('pagina.dispositivos.afericoes.col.tipo')}</PlainHeader>,
        id: 'tipo',
        Cell: ({ row }: CellProps<AfericaoDispositivoList>) => (
          <StackedCell primary={row.original.tipoInformacao?.descricao ?? ''} />
        ),
      },
      {
        Header: () => <PlainHeader>{t('pagina.dispositivos.afericoes.col.empresa')}</PlainHeader>,
        id: 'empresa',
        Cell: ({ row }: CellProps<AfericaoDispositivoList>) => (
          <StackedCell primary={row.original.empresa?.nome ?? ''} />
        ),
      },
      {
        // Só o número, sem link nem dado de paciente (LGPD).
        Header: () => (
          <PlainHeader>{t('pagina.dispositivos.afericoes.col.atendimento')}</PlainHeader>
        ),
        id: 'atendimento',
        cellWidth: '140px',
        align: 'end',
        Cell: ({ row }: CellProps<AfericaoDispositivoList>) => (
          <StackedCell tabular primary={row.original.idAtendimento ?? ''} />
        ),
      },
    ],
    [t]
  )

  if (isError && !response) return null

  return (
    <CustomTable<AfericaoDispositivoList>
      id='kt_table_dispositivo_afericoes'
      ariaLabel={t('pagina.dispositivos.aba.afericoes')}
      itemLabel={{ singular: 'aferição', plural: 'aferições' }}
      data={response?.data ?? []}
      columns={colunas}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      emptyState={<TableEmptyState title={t('pagina.dispositivos.afericoes.vazio_titulo')} />}
      queryHooks={{ useQueryRequest, useQueryResponse }}
    />
  )
}

export const AfericoesAba = ({ idDispositivo }: { idDispositivo: number }) => {
  const { t } = useT()
  const { data, isLoading, error, refetch } = useQuery(
    ['dispositivo-afericoes-resumo', idDispositivo],
    () => getAfericoesResumo(idDispositivo),
    { refetchOnWindowFocus: false, retry: false }
  )

  if (isLoading) {
    return <p className='text-gray-700'>{t('common.loading')}</p>
  }

  if (error) {
    return rotaIndisponivel(error) ? (
      <div className='rounded bg-gray-100 text-gray-700 p-5' role='note'>
        {t('pagina.dispositivos.afericoes.indisponivel')}
      </div>
    ) : (
      <TableErrorState onRetry={() => refetch()} />
    )
  }

  if (!data || data.quantidade === 0) {
    return (
      <div className='rounded bg-gray-100 text-gray-700 p-5' role='note'>
        <strong className='d-block text-gray-900 mb-1'>
          {t('pagina.dispositivos.afericoes.vazio_titulo')}
        </strong>
        {t('pagina.dispositivos.afericoes.vazio_descricao')}
      </div>
    )
  }

  return (
    <>
      <p className='rounded bg-gray-100 text-gray-900 p-5 mb-6'>
        {t('pagina.dispositivos.afericoes.resumo', {
          quantidade: data.quantidade,
          ultima: formatDateTime(data.ultimaAfericao ?? undefined),
        })}
      </p>
      <Providers idDispositivo={idDispositivo}>
        <TabelaAfericoes />
      </Providers>
    </>
  )
}
